import {
  DataTypes,
  Fields,
  getLTLQuotesMandatoryFields,
  getQuotesMandatoryFields,
  orderExpeditedMandatoryFields,
  orderLTLFinalMileMandatoryFields,
  orderLTLMandatoryFields,
  orderMandatoryFields,
} from "../pages/Order-management/CustomerOrder/CustomerOrder-types";
import {
  DROPDOWN_FIELD_KEYS,
  ITEM_DIMENSION_KEYS,
  ORDER_FIELD_LABELS,
} from "./constants";
import { MANDATORY_SUFFIX } from "./message";

/**
 * Shared mandatory-field validation for the order/quote screens.
 *
 * This is a faithful transcription of the gating that already lived inside the
 * `isGetQuotesButtonDisabled` / `isGetLTLQuotesButtonDisabled` / `isButtonDisabled`
 * memos in CustomerOrder/index.tsx, TraditionalOrder.tsx and QuickQuote.tsx. Nothing
 * new is required here — the difference is that the caller now learns *which* values
 * are missing instead of only whether the button should be disabled.
 *
 * The three screens disagree on a handful of conditions (see the flags on
 * `OrderValidationContext`), so those are parameters rather than baked-in behaviour.
 */

const labelForField = (key: string): string => ORDER_FIELD_LABELS[key] || key;

export const mandatoryMessage = (label: string): string =>
  `${label}${MANDATORY_SUFFIX}`;

/**
 * True for a message this validation wrote.
 *
 * The screens keep several legacy "abort if any error is set" guards that predate this
 * feature. Those must keep ignoring mandatory messages: `validateOrderForm` re-derives
 * them from scratch on every click and owns that decision, whereas the guards read the
 * `errors` object from a stale render closure and would otherwise block on a message
 * that has just been cleared.
 */
export const isMandatoryMessage = (value: unknown): boolean =>
  typeof value === "string" && value.endsWith(MANDATORY_SUFFIX);

export type OrderIntent = "saveOrder" | "getQuote" | "getLtlQuote";

export interface MissingEntry {
  key: string;
  label: string;
}

export interface PackageIssue {
  packageIndex: number;
  columnKeys: string[];
}

export interface ItemIssue {
  rowIndex: number;
  columnKeys: string[];
  /** Problems in this item's package sub-rows, reported against the package's own cells. */
  packageIssues: PackageIssue[];
}

export interface ValidationResult {
  /** Blank scalar fields and dropdowns, unordered — the caller sorts by DOM position. */
  missing: MissingEntry[];
  /**
   * Mandatory fields that hold a value but already carry a field-level error (bad zip,
   * bad phone, date ordering). These still block the action, but they keep their own
   * more specific message rather than being relabelled "is mandatory".
   */
  invalid: MissingEntry[];
  /** Per-row handling-unit problems. */
  itemIssues: ItemIssue[];
  /** True when the handling-unit table is empty and at least one row is required. */
  itemsEmpty: boolean;
  isValid: boolean;
}

export interface OrderValidationContext {
  intent: OrderIntent;
  fields: Fields;
  data: DataTypes[];
  errors: Record<string, string>;
  orderTypeName?: string | null;
  /** True for Final Mile (and while no order type is chosen). */
  notLTL: boolean;

  customerId?: string | null;
  orderTypeId?: string | null;
  serviceId?: string | null;
  agent?: { id?: string | null; agentType?: string | null } | null;
  agentHub?: { id?: string | null } | null;

  /**
   * Per-screen switches — each mirrors a condition that already differed between
   * the three copies of the gating memos.
   */
  /** TraditionalOrder's LTL-quote and save gates do not require these. */
  requireOrderTypeId?: boolean;
  requireServiceId?: boolean;
  /**
   * The quote gates additionally refuse to run while a mandatory field carries an error
   * string. The save gates instead check that the whole `errors` object is clear, which
   * `handleSave`'s own pre-existing guard continues to enforce.
   */
  quoteGateChecksErrors?: boolean;
  /** LTL quote gate: always require itemClass (QuickQuote's LTL variant). */
  ltlQuoteRequiresItemClass?: boolean;
}

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null && String(value).trim() !== "";

/**
 * Mirrors the `arePackagesValid` helper duplicated across the three screens: a package
 * row is fine when it is entirely blank, but once any of its three values is filled the
 * other two become required.
 */
const PACKAGE_KEYS = ["description", "quantity", "weight"];

/**
 * Every package row must be complete.
 *
 * The original `arePackagesValid` ignored a row whose three values were all blank, on the
 * reading that an untouched row was not really there. But a row only exists because the
 * user pressed Add Package, and the card marks all three with a red asterisk — so a
 * freshly added, still-empty package is exactly the case that needs reporting.
 */
const findPackageIssues = (item: DataTypes): PackageIssue[] => {
  if (!item.package || item.package.length === 0) return [];

  const issues: PackageIssue[] = [];
  item.package.forEach((pkg: any, packageIndex: number) => {
    const columnKeys = PACKAGE_KEYS.filter((key) => !hasValue(pkg?.[key]));
    if (columnKeys.length > 0) issues.push({ packageIndex, columnKeys });
  });
  return issues;
};

/** Resolves the mandatory-field list for the save gate, matching the existing branch chain. */
const saveFieldsForOrderType = (
  notLTL: boolean,
  orderTypeName?: string | null
): (keyof Fields)[] => {
  if (notLTL) return orderMandatoryFields;
  if (orderTypeName === "Expedited") return orderExpeditedMandatoryFields;
  if (orderTypeName === "LTL") return orderLTLMandatoryFields;
  return orderLTLFinalMileMandatoryFields;
};

/**
 * Collects every handling-unit problem. `columns` lists the keys required for the
 * current intent; `itemDimension` is expanded into its three children so the caller can
 * highlight the exact sub-input.
 */
const collectItemIssues = (
  data: DataTypes[],
  columns: string[],
  checkPackages: boolean
): ItemIssue[] => {
  const issues: ItemIssue[] = [];

  data.forEach((item, rowIndex) => {
    const columnKeys: string[] = [];

    columns.forEach((column) => {
      if (column === "itemDimension") {
        ITEM_DIMENSION_KEYS.forEach((side) => {
          if (!hasValue((item.itemDimension as any)?.[side])) {
            columnKeys.push(side);
          }
        });
        return;
      }
      if (!hasValue((item as any)[column])) columnKeys.push(column);
    });

    // Reported against the package's own cells — flagging the item's Description here
    // would point at a field that is filled while the real gap sits in a sub-row.
    const packageIssues = checkPackages ? findPackageIssues(item) : [];

    if (columnKeys.length > 0 || packageIssues.length > 0) {
      issues.push({ rowIndex, columnKeys, packageIssues });
    }
  });

  return issues;
};

/**
 * Drops flags for handling-unit cells that now hold a value.
 *
 * Item cells never pass through `handleFieldChange`, so nothing else clears them. This
 * matters most when a saved item is picked from the Description dropdown: one selection
 * fills quantity, weight, dimensions, class and NMFC# at once, and every one of those
 * highlights has to go with it.
 *
 * Only ever removes flags — a field the user empties again is not re-reported until the
 * next Get Quote / Save. Returns the same reference when nothing changed, so callers can
 * drive this from an effect without looping.
 */
export const pruneItemIssues = (
  invalidCells: Record<number, string[]>,
  data: DataTypes[]
): Record<number, string[]> => {
  const next: Record<number, string[]> = {};
  let changed = false;

  Object.entries(invalidCells).forEach(([rowKey, columnKeys]) => {
    const rowIndex = Number(rowKey);
    const item = data?.[rowIndex];

    if (!item) {
      // The row was deleted; its flags go with it.
      changed = true;
      return;
    }

    const stillMissing = columnKeys.filter((key) =>
      ITEM_DIMENSION_KEYS.includes(key)
        ? !hasValue((item.itemDimension as any)?.[key])
        : !hasValue((item as any)[key])
    );

    if (stillMissing.length !== columnKeys.length) changed = true;
    if (stillMissing.length > 0) next[rowIndex] = stillMissing;
  });

  return changed ? next : invalidCells;
};

/** The package-row equivalent of {@link pruneItemIssues}, keyed row -> package -> keys. */
export const prunePackageIssues = (
  invalidPackageCells: Record<number, Record<number, string[]>>,
  data: DataTypes[]
): Record<number, Record<number, string[]>> => {
  const next: Record<number, Record<number, string[]>> = {};
  let changed = false;

  Object.entries(invalidPackageCells).forEach(([rowKey, packages]) => {
    const rowIndex = Number(rowKey);
    const item = data?.[rowIndex];
    if (!item) {
      changed = true;
      return;
    }

    const nextPackages: Record<number, string[]> = {};
    Object.entries(packages).forEach(([pkgKey, columnKeys]) => {
      const packageIndex = Number(pkgKey);
      const pkg = (item.package as any)?.[packageIndex];
      if (!pkg) {
        changed = true;
        return;
      }

      const stillMissing = columnKeys.filter((key) => !hasValue(pkg?.[key]));
      if (stillMissing.length !== columnKeys.length) changed = true;
      if (stillMissing.length > 0) nextPackages[packageIndex] = stillMissing;
    });

    if (Object.keys(nextPackages).length > 0) next[rowIndex] = nextPackages;
  });

  return changed ? next : invalidPackageCells;
};

/**
 * Drops mandatory messages for scalar fields that now hold a value.
 *
 * `handleFieldChange` clears a field the user types into, but several paths fill many
 * fields at once without going through it — picking a saved address hydrates the whole
 * origin or destination block, and zip-code lookup, hub selection and quote hydration do
 * the same. Those messages have to clear with the values.
 *
 * Only touches messages this validation wrote, so field-level text ("Please enter a valid
 * zip code.") survives. Dropdown keys are absent from `values` and are left to
 * `clearMandatory`. Returns the same reference when nothing changed.
 */
export const pruneMandatoryMessages = (
  errors: Record<string, string>,
  values: Record<string, any>
): Record<string, string> => {
  let changed = false;
  const next = { ...errors };

  Object.keys(next).forEach((key) => {
    if (isMandatoryMessage(next[key]) && hasValue(values?.[key])) {
      next[key] = "";
      changed = true;
    }
  });

  return changed ? next : errors;
};

export function validateOrderForm(
  ctx: OrderValidationContext
): ValidationResult {
  const {
    intent,
    fields,
    data,
    errors,
    orderTypeName,
    notLTL,
    customerId,
    orderTypeId,
    serviceId,
    agent,
    agentHub,
    requireOrderTypeId = true,
    requireServiceId = true,
    quoteGateChecksErrors = false,
    ltlQuoteRequiresItemClass = false,
  } = ctx;

  const missing: MissingEntry[] = [];
  const invalid: MissingEntry[] = [];
  const addMissing = (key: string) =>
    missing.push({ key, label: labelForField(key) });

  // Item Class is not mandatory for Final Mile and Expedited orders. NMFC# is never
  // mandatory, on any order type.
  const isItemClassOptional =
    orderTypeName === "Final Mile" || orderTypeName === "Expedited";

  // --- Dropdowns -----------------------------------------------------------
  // Some of these gate each other. Only report a field the user can actually act on:
  // the Service Level picker stays disabled until an order type is chosen, so asking for
  // it first would be a dead end — the order-type message is the actionable one. The
  // Expedited agent/hub pair below follows the same rule.
  if (!customerId) addMissing(DROPDOWN_FIELD_KEYS.customer);

  const orderTypeMissing = requireOrderTypeId && !orderTypeId;
  if (orderTypeMissing) addMissing(DROPDOWN_FIELD_KEYS.orderType);

  // Suppressed only while the order type is itself being reported, so the form is still
  // blocked and the user is never left without something to act on.
  if (requireServiceId && !serviceId && !orderTypeMissing) {
    addMissing(DROPDOWN_FIELD_KEYS.service);
  }

  // --- Scalar fields -------------------------------------------------------
  let requiredFields: (keyof Fields)[];
  if (intent === "saveOrder") {
    requiredFields = saveFieldsForOrderType(notLTL, orderTypeName);
  } else if (intent === "getLtlQuote") {
    requiredFields = getLTLQuotesMandatoryFields;
  } else {
    requiredFields = getQuotesMandatoryFields;
  }

  requiredFields.forEach((field) => {
    const key = field as string;
    if (!hasValue(fields[field])) {
      addMissing(key);
      return;
    }
    // Filled, but the field already failed its own format check. Keep that message.
    if (intent !== "saveOrder" && quoteGateChecksErrors && !!errors?.[key]) {
      invalid.push({ key, label: labelForField(key) });
    }
  });

  // Expedited additionally requires an agent, and a hub for non-LTL agents.
  if (intent === "saveOrder" && !notLTL && orderTypeName === "Expedited") {
    if (!agent?.id) {
      addMissing(DROPDOWN_FIELD_KEYS.agent);
    } else if (agent.agentType && agent.agentType !== "LTL" && !agentHub?.id) {
      addMissing(DROPDOWN_FIELD_KEYS.agentHub);
    }
  }

  // --- Handling units ------------------------------------------------------
  const itemsEmpty = !data || data.length === 0;

  /**
   * Save and quote ask for the same handling-unit values.
   *
   * The save gate used to check a narrower set, omitting Description and Item Dimension.
   * For the quote-based order types that was harmless — a quote has to be selected before
   * saving, the quote gate already demands both, and editing an item clears the selected
   * quote. Expedited has no quote step at all, so nothing enforced them there and an order
   * could be booked with those cells blank despite being marked mandatory in the UI.
   */
  const itemColumns = [
    "description",
    "handlingUnit",
    "quantity",
    "weight",
    "itemDimension",
    "packageType",
  ];
  const requireItemClass =
    intent === "getLtlQuote"
      ? ltlQuoteRequiresItemClass || !isItemClassOptional
      : !isItemClassOptional;
  if (requireItemClass) itemColumns.push("itemClass");

  /**
   * Package rows are checked on every gate. `ltlQuoteRequiresItemClass` used to switch
   * this off for the Add Order LTL quote, which meant a half-filled package row slipped
   * through there; that flag now governs Item Class only.
   */
  const checkPackages = true;

  const itemIssues = itemsEmpty
    ? []
    : collectItemIssues(data, itemColumns, checkPackages);

  const isValid =
    missing.length === 0 &&
    invalid.length === 0 &&
    !itemsEmpty &&
    itemIssues.length === 0;

  return {
    missing,
    invalid,
    itemIssues,
    itemsEmpty,
    isValid,
  };
}
